import React from 'react'
import classNamesBind from 'classnames/bind'
import Link from 'next/link'

import ProfileImage from '@dashboard/components/ProfileImage'
import ReactiveOverlay from '@dashboard/components/ReactiveOverlay'

import { fullName, getInitials } from '@dashboard/lib/profile'
import { getRandomProfileImage } from '@dashboard/lib/image'
import {
  AuthUserProfile,
  Profile,
  ProfileContent,
  LoadingStates,
} from '@dashboard/lib/types'
import { convertDataTags } from '@dashboard/lib/api'
import css from './styles.module.scss'
const classNames = classNamesBind.bind(css)

export interface OwnProps {
  profile: AuthUserProfile | Profile
  size: number
  basicEmpty?: boolean
  blur?: boolean
  grey?: boolean
  hover?: boolean
  hide?: boolean
  hoverOverview?: boolean
  linkToProfile?: boolean
  showDiscInfo?: boolean
  initials?: boolean
  pull?: boolean
  shadow?: boolean
  lightShadow?: boolean
  whiteBorder?: boolean
  showArchetype?: boolean
  tooltip?: React.ReactNode | null
  isOverview?: boolean
  square?: boolean
  noWrap?: boolean
}

export interface StateProps {
  content: ProfileContent
  contentState: LoadingStates
}

export interface DispatchProps {
  getProfileData: (id: string, types: string[][]) => void
}

interface State {
  randomURL: string
}

type Props = OwnProps & StateProps & DispatchProps

export default class ProfileBubble extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      randomURL: '',
    }
  }

  componentDidMount() {
    const { blur, contentState, getProfileData, profile, hoverOverview } =
      this.props

    if (blur) this.setState({ randomURL: getRandomProfileImage() })

    if (contentState == 'default' && hoverOverview)
      getProfileData(profile.id, [['profile', 'overview']])
  }

  render() {
    const { linkToProfile, profile } = this.props

    if (linkToProfile) {
      return (
        <Link
          href="/p/[profileId]"
          as={`/p/${profile.id}`}
          className={classNames('link')}
          passHref
        >
          {this.renderHoverOverlay()}
        </Link>
      )
    }

    return this.renderHoverOverlay()
  }

  renderHoverOverlay() {
    const { hover, hoverOverview } = this.props

    if (hover || hoverOverview) {
      return (
        <ReactiveOverlay
          withTransform
          overlayGap={12}
          preferredDirection={'topRight'}
          overlayContent={this.renderHover()}
        >
          {this.renderContent()}
        </ReactiveOverlay>
      )
    }

    return this.renderContent()
  }

  renderContent() {
    const { size, shadow, pull, blur, whiteBorder, lightShadow } = this.props

    return (
      <div
        style={{ width: size, minWidth: size, height: size, minHeight: size }}
        className={classNames('container', {
          shadow,
          lightShadow,
          pull,
          blur,
          whiteBorder,
        })}
      >
        {this.renderProfileImage()}
        {this.renderArchetype()}
      </div>
    )
  }

  renderProfileImage() {
    const { profile, size, initials, basicEmpty, blur, hide, grey, square } =
      this.props
    const { randomURL } = this.state
    const large = size > 100
    const discType = grey ? '' : profile.personality?.disc_type || ''

    if (hide) {
      return <div className={classNames('plain', { large, square })}>?</div>
    }

    if (blur) {
      return (
        <div className={classNames('blur')}>
          <ProfileImage
            photoUrl={randomURL}
            size={size}
            fullName={fullName(profile)}
            discType={profile.personality?.disc_type}
          />
        </div>
      )
    }

    if (basicEmpty || (profile.photo_url && !initials && this.isValidURL())) {
      return (
        <ProfileImage
          photoUrl={profile.photo_url}
          size={size}
          fullName={fullName(profile)}
          discType={profile.personality?.disc_type}
        />
      )
    }

    return (
      <div className={classNames('plain', discType, { large, square })}>
        {getInitials(profile).toUpperCase()}
      </div>
    )
  }

  renderArchetype() {
    const { profile, showArchetype } = this.props

    if (!profile?.personality || !showArchetype) return null

    const discType = profile.personality?.disc_type || ''
    const archetype = profile.personality?.archetype || ''

    return (
      <div className={classNames('archetype')}>
        <span className={discType}>{archetype}</span>
      </div>
    )
  }

  renderHover() {
    const { profile, hover, tooltip, hoverOverview, isOverview, noWrap } =
      this.props
    const discType = profile.personality?.disc_type || ''
    const archetype = profile.personality?.archetype || ''

    if (!hover && !hoverOverview) return null

    if (hoverOverview) {
      return (
        <div className={classNames('tip', 'overview', discType, { noWrap })}>
          <div className={classNames('name')}>{fullName(profile)}</div>
          <div
            className={classNames('info', 'fullname', discType)}
          >{`${archetype} (${discType})`}</div>

          {this.renderOverview()}
        </div>
      )
    }

    return (
      <div
        className={classNames(
          'tip',
          discType,
          { noWrap },
          {
            overview: isOverview,
          },
        )}
      >
        <span>{tooltip || fullName(profile)}</span>
        {this.renderDiscInfo()}
      </div>
    )
  }

  renderOverview() {
    const overview = this.getOverview()

    if (!overview) return null

    return <div className={classNames('overview')}>{overview}</div>
  }

  renderDiscInfo() {
    const { showDiscInfo, profile } = this.props
    const { archetype, disc_type } = profile.personality || {}

    if (!showDiscInfo) return null

    return <span>{`${archetype} (${disc_type})`}</span>
  }

  // Functions
  isValidURL = () => {
    const { photo_url } = this.props.profile

    return (
      photo_url && photo_url.length > 0 && !photo_url.startsWith('data:image/')
    )
  }

  getOverview() {
    const { content } = this.props
    return (content[convertDataTags(['profile', 'overview'])] || [])[0]?.text
  }
}
